export type ThemeOption = 'light' | 'dark' | 'system';

class ThemeServiceClass {
  private STORAGE_KEY = 'docuid_theme';
  private mediaQuery: MediaQueryList | null = null;
  private currentListener: (() => void) | null = null;

  getTheme(): ThemeOption {
    if (typeof window === 'undefined') return 'system';
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') {
      return saved;
    }
    return 'system';
  }

  getResolvedTheme(): ThemeOption {
    const theme = this.getTheme();
    if (theme === 'system') {
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return isDark ? 'dark' : 'light';
    }
    return theme;
  }

  setTheme(theme: ThemeOption): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(this.STORAGE_KEY, theme);
    this.applyTheme();
  }

  applyTheme(): void {
    if (typeof window === 'undefined') return;
    const theme = this.getTheme();
    let isDark = false;

    // Clean up old listener
    if (this.mediaQuery && this.currentListener) {
      this.mediaQuery.removeEventListener('change', this.currentListener);
      this.currentListener = null;
    }

    if (theme === 'system') {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      isDark = this.mediaQuery.matches;

      // Listen for system changes dynamically
      this.currentListener = () => {
        const isSystemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches;
        this.updateDOM(isSystemTheme);
      };
      this.mediaQuery.addEventListener('change', this.currentListener);
    } else {
      isDark = theme === 'dark';
    }

    this.updateDOM(isDark);
  }

  private updateDOM(isDark: boolean): void {
    const root = document.documentElement;
    const body = document.body;

    if (isDark) {
      root.classList.add('dark');
      body.classList.add('dark');
      root.style.colorScheme = 'dark';
    } else {
      root.classList.remove('dark');
      body.classList.remove('dark');
      root.style.colorScheme = 'light';
    }
  }
}

export const ThemeService = new ThemeServiceClass();
